import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddRounded, SchoolOutlined } from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useCurrentMembership } from '../../../hooks/use-current-membership';
import { errorParams, errorReason, errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';
import {
  programEnrollmentApi,
  type ProgramEnrollmentDto,
  type ProgramEnrollmentStatus,
} from '../../../lib/api/program-enrollment.api';
import {
  canManageProgramEnrollment,
  canRegisterProgramEnrollment,
  programEnrollmentActions,
  WITHDRAW_REASON_MIN,
} from './program-enrollment-actions.model';
import { GraduateProgramDialog } from './GraduateProgramDialog';

// ─── Matrícula a programa (H5) ──────────────────────────────────────────────
//
// La relación de la persona con cada programa: registrarla, pausarla,
// reactivarla, egresarla y retirarla, SIN DEPENDER DE UNA ADMISIÓN. Antes, un
// alumno importado (sin postulación) no tenía dónde registrar su retiro: vivía
// solo en Admisiones (hallazgo de #681).
//
// Cada cambio queda en la auditoría (lo hace el servidor), y el estado del
// alumno —Activo, Inactivo, Egresado— y su portal se recalculan con la regla
// de siempre.

const STATUS_COLOR: Record<ProgramEnrollmentStatus, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  on_leave: 'warning',
  graduated: 'info',
  withdrawn: 'default',
};

interface NamedDoc {
  id: string;
  name: string;
  status?: string;
}

export function ProgramEnrollmentTab({ personId }: { personId: string }): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const membership = useCurrentMembership();
  // Registrar: Registro y administración. Pausar, reactivar, egresar y retirar:
  // también el decanato (program-enrollment-actions.model).
  const canRegister = canRegisterProgramEnrollment(membership?.roles);
  const canManage = canManageProgramEnrollment(membership?.roles);
  const queryClient = useQueryClient();

  const [programs, setPrograms] = useState<NamedDoc[]>([]);
  const [terms, setTerms] = useState<NamedDoc[]>([]);
  useEffect(() => {
    if (!currentTenantId) return;
    const u1 = onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) =>
      setPrograms(
        snap.docs.map((d) => ({
          id: d.id,
          name: (d.data()['name'] as string) ?? d.id,
          status: d.data()['status'] as string,
        })),
      ),
    );
    const u2 = onSnapshot(collection(db, `tenants/${currentTenantId}/terms`), (snap) =>
      setTerms(snap.docs.map((d) => ({ id: d.id, name: (d.data()['name'] as string) ?? d.id }))),
    );
    return () => {
      u1();
      u2();
    };
  }, [currentTenantId]);
  const programName = (id: string) => programs.find((p) => p.id === id)?.name ?? id;
  const termName = (id: string) => terms.find((x) => x.id === id)?.name ?? id;

  const q = useQuery({
    queryKey: ['person-program-enrollments', currentTenantId, personId],
    enabled: !!currentTenantId && !!personId,
    queryFn: () =>
      programEnrollmentApi.listStudentProgramEnrollments({
        tenantId: currentTenantId!,
        studentId: personId,
      }),
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];

  // Lo que cambia con una transición: la lista, el avance, la ficha (etapa de
  // la persona) y la lista de Alumnos.
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['person-program-enrollments'] }),
      queryClient.invalidateQueries({ queryKey: ['person-degree-audit'] }),
      queryClient.invalidateQueries({ queryKey: ['credential-progress'] }),
    ]);
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<{
    pe: ProgramEnrollmentDto;
    onLeave: boolean;
  } | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<ProgramEnrollmentDto | null>(null);
  const [graduateTarget, setGraduateTarget] = useState<ProgramEnrollmentDto | null>(null);

  if (q.isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
          {t('programEnrollment.intro')}
        </Typography>
        {canRegister && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setCreateOpen(true)}
          >
            {t('programEnrollment.register')}
          </Button>
        )}
      </Stack>

      {q.isError && <Alert severity="error">{errorText(q.error)}</Alert>}

      {items.length === 0 && !q.isError && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <SchoolOutlined sx={{ fontSize: 56, opacity: 0.25, mb: 1 }} />
          <Typography variant="h6">{t('programEnrollment.empty')}</Typography>
          <Typography variant="body2">{t('programEnrollment.emptyDesc')}</Typography>
        </Box>
      )}

      {items.map((pe) => {
        const actions = programEnrollmentActions(pe.status, canManage);
        return (
          <Card key={pe.id} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight={700}>
                  {programName(pe.programId)}
                </Typography>
                <Chip
                  size="small"
                  color={STATUS_COLOR[pe.status]}
                  label={t(`career.status.${pe.status}`)}
                />
                <Box sx={{ flexGrow: 1 }} />
                {actions.includes('pause') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setLeaveTarget({ pe, onLeave: true })}
                  >
                    {t('career.actions.pause')}
                  </Button>
                )}
                {actions.includes('reactivate') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setLeaveTarget({ pe, onLeave: false })}
                  >
                    {t('career.actions.reactivate')}
                  </Button>
                )}
                {actions.includes('graduate') && (
                  <Button size="small" variant="outlined" onClick={() => setGraduateTarget(pe)}>
                    {t('career.actions.graduate')}
                  </Button>
                )}
                {actions.includes('withdraw') && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setWithdrawTarget(pe)}
                  >
                    {t('programEnrollment.withdraw')}
                  </Button>
                )}
              </Stack>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('programEnrollment.intake', { term: termName(pe.intakeTermId) })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('programEnrollment.startedAt', { date: formatDate(pe.startedAt) })}
                </Typography>
                {pe.endedAt && (
                  <Typography variant="body2" color="text.secondary">
                    {t('programEnrollment.endedAt', { date: formatDate(pe.endedAt) })}
                  </Typography>
                )}
              </Stack>
              {pe.status === 'withdrawn' && pe.endReason && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('programEnrollment.withdrawnReason', { reason: pe.endReason })}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })}

      {createOpen && currentTenantId && (
        <CreateDialog
          tenantId={currentTenantId}
          personId={personId}
          programs={programs.filter(
            (p) => p.status !== 'archived' && !items.some((i) => i.programId === p.id),
          )}
          terms={terms}
          onClose={() => setCreateOpen(false)}
          onDone={refresh}
        />
      )}
      {leaveTarget && currentTenantId && (
        <LeaveDialog
          tenantId={currentTenantId}
          target={leaveTarget}
          programName={programName(leaveTarget.pe.programId)}
          onClose={() => setLeaveTarget(null)}
          onDone={refresh}
        />
      )}
      {withdrawTarget && currentTenantId && (
        <WithdrawDialog
          tenantId={currentTenantId}
          pe={withdrawTarget}
          programName={programName(withdrawTarget.programId)}
          onClose={() => setWithdrawTarget(null)}
          onDone={refresh}
        />
      )}
      {graduateTarget && currentTenantId && (
        <GraduateProgramDialog
          open
          onClose={() => setGraduateTarget(null)}
          onDone={refresh}
          tenantId={currentTenantId}
          personId={personId}
          programEnrollmentId={graduateTarget.id}
          programId={graduateTarget.programId}
          programName={programName(graduateTarget.programId)}
        />
      )}
    </Stack>
  );
}

// ─── Registrar una matrícula a programa ─────────────────────────────────────

function CreateDialog({
  tenantId,
  personId,
  programs,
  terms,
  onClose,
  onDone,
}: {
  tenantId: string;
  personId: string;
  programs: NamedDoc[];
  terms: NamedDoc[];
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [programId, setProgramId] = useState('');
  const [termId, setTermId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // EL REQUISITO DE ESCALERA: si el programa se apoya en otro que la persona no
  // completó, el servidor lo rechaza; se puede registrar igual declarando por qué.
  const [needsOverride, setNeedsOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const sortedTerms = useMemo(
    () => [...terms].sort((a, b) => b.name.localeCompare(a.name)),
    [terms],
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await programEnrollmentApi.create({
        tenantId,
        studentId: personId,
        programId,
        intakeTermId: termId,
        ...(needsOverride ? { stackEntryOverride: { reason: overrideReason.trim() } } : {}),
      });
      await onDone();
      onClose();
    } catch (e) {
      if (errorReason(e) === 'program_enrollment.predecessor_incomplete') setNeedsOverride(true);
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('programEnrollment.registerTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>{t('programEnrollment.registerBody')}</DialogContentText>
          <TextField
            select
            label={t('programEnrollment.program')}
            value={programId}
            onChange={(e) => setProgramId(e.target.value)}
            disabled={busy}
          >
            {programs.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('programEnrollment.intakeTerm')}
            helperText={t('programEnrollment.intakeTermHelp')}
            value={termId}
            onChange={(e) => setTermId(e.target.value)}
            disabled={busy}
          >
            {sortedTerms.map((x) => (
              <MenuItem key={x.id} value={x.id}>
                {x.name}
              </MenuItem>
            ))}
          </TextField>
          {needsOverride && (
            <TextField
              multiline
              minRows={2}
              label={t('programEnrollment.overrideLabel')}
              helperText={t('programEnrollment.overrideHelp')}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              disabled={busy}
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={
            busy || !programId || !termId || (needsOverride && overrideReason.trim().length === 0)
          }
        >
          {t('programEnrollment.register')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Pausar / reactivar ─────────────────────────────────────────────────────

function LeaveDialog({
  tenantId,
  target,
  programName,
  onClose,
  onDone,
}: {
  tenantId: string;
  target: { pe: ProgramEnrollmentDto; onLeave: boolean };
  programName: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await programEnrollmentApi.setLeave({
        tenantId,
        programEnrollmentId: target.pe.id,
        onLeave: target.onLeave,
      });
      await onDone();
      onClose();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {target.onLeave
          ? t('programEnrollment.pauseTitle')
          : t('programEnrollment.reactivateTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {target.onLeave
            ? t('programEnrollment.pauseBody', { program: programName })
            : t('programEnrollment.reactivateBody', { program: programName })}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy}>
          {target.onLeave ? t('career.actions.pause') : t('career.actions.reactivate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Retirar ────────────────────────────────────────────────────────────────

function WithdrawDialog({
  tenantId,
  pe,
  programName,
  onClose,
  onDone,
}: {
  tenantId: string;
  pe: ProgramEnrollmentDto;
  programName: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // QUIEN ENTRÓ POSTULANDO SE RETIRA DESDE ADMISIONES: ahí se calcula lo
  // devuelto y lo retenido de su plan de pago. El servidor lo dice y trae la
  // admisión, para ir directo.
  const [viaAdmission, setViaAdmission] = useState<string | null>(null);
  const tooShort = reason.trim().length < WITHDRAW_REASON_MIN;

  // ANTES DE CONFIRMAR, QUÉ NO CAMBIA EL RETIRO: los planes de pago activos de
  // ese programa (solo con Finanzas encendido) y las secciones en que sigue
  // inscrito. Solo cifras, del servidor. El retiro no escribe nada en ellos.
  const previewQ = useQuery({
    queryKey: ['program-withdrawal-preview', tenantId, pe.id],
    queryFn: () =>
      programEnrollmentApi.getWithdrawalPreview({ tenantId, programEnrollmentId: pe.id }),
  });
  const preview = previewQ.data;
  // Si el servidor ya sabe que el retiro va a Admisiones, se dice de entrada.
  useEffect(() => {
    if (preview?.viaAdmission) setViaAdmission(preview.viaAdmission.admissionId);
  }, [preview]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await programEnrollmentApi.withdraw({
        tenantId,
        programEnrollmentId: pe.id,
        reason: reason.trim(),
      });
      await onDone();
      onClose();
    } catch (e) {
      if (errorReason(e) === 'program_enrollment.withdraw_via_admission') {
        setViaAdmission(String(errorParams(e)?.['admissionId'] ?? ''));
      }
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('programEnrollment.withdrawTitle', { program: programName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText>{t('programEnrollment.withdrawBody')}</DialogContentText>
          {previewQ.isLoading && <CircularProgress size={20} />}
          {preview && !preview.viaAdmission && (preview.activePaymentPlans ?? 0) > 0 && (
            <Alert severity="warning">
              {t('programEnrollment.warnPlans', { count: preview.activePaymentPlans ?? 0 })}
            </Alert>
          )}
          {preview && !preview.viaAdmission && preview.enrolledSections > 0 && (
            <Alert severity="warning">
              {t('programEnrollment.warnSections', { count: preview.enrolledSections })}
            </Alert>
          )}
          {viaAdmission && !error && (
            <Alert severity="info">
              {t('errors.program_enrollment.withdraw_via_admission', { ns: 'common' })}{' '}
              <Link component={RouterLink} to={`/admissions/${viaAdmission}`}>
                {t('programEnrollment.goToAdmission')}
              </Link>
            </Alert>
          )}
          <TextField
            multiline
            minRows={3}
            label={t('programEnrollment.withdrawReasonLabel')}
            helperText={t('programEnrollment.withdrawReasonHelp', { min: WITHDRAW_REASON_MIN })}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={reason.length > 0 && tooShort}
            disabled={busy || !!viaAdmission}
          />
          {error && (
            <Alert severity={viaAdmission ? 'info' : 'error'}>
              {error}
              {viaAdmission && (
                <>
                  {' '}
                  <Link component={RouterLink} to={`/admissions/${viaAdmission}`}>
                    {t('programEnrollment.goToAdmission')}
                  </Link>
                </>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void submit()}
          disabled={busy || tooShort || !!viaAdmission}
        >
          {t('programEnrollment.withdraw')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
